import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Building2, Puzzle, Zap, Bot, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { companiesApi, type Company } from "@/api/companies";
import { mcpApi, type McpCatalogueItem } from "@/api/mcp";
import { skillsApi, type SkillCatalogueItem } from "@/api/skills";
import { queryKeys } from "@/lib/query-keys";

export type CommandResultKind = "tenant" | "agent" | "skill" | "mcp";

export interface CommandResult {
  kind: CommandResultKind;
  id: string;
  label: string;
  subtitle?: string;
  to: string;
}

/**
 * Data provider hook for the palette. Currently wires tenants, skills, and
 * MCP catalogue entries to existing endpoints. Cross-tenant agent search is
 * not yet wired — see TODO.
 */
function useCommandPaletteResults(enabled: boolean, query: string) {
  const { data: companies = [] } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: companiesApi.list,
    enabled,
  });
  const { data: mcps = [] } = useQuery({
    queryKey: queryKeys.mcpCatalogue.all,
    queryFn: () => mcpApi.listCatalogue(),
    enabled,
  });
  const { data: skills = [] } = useQuery({
    queryKey: queryKeys.skillCatalogue.all,
    queryFn: skillsApi.listCatalogue,
    enabled,
  });

  // TODO(cross-tenant agent search): there's no single endpoint today; either
  // add GET /admin/agents or fan out per company. For now agents are omitted
  // from palette results so the interface stays honest.
  const q = query.trim().toLowerCase();
  return useMemo<CommandResult[]>(() => {
    const matches: CommandResult[] = [];
    const hit = (haystack: string) => !q || haystack.toLowerCase().includes(q);

    for (const c of companies as Company[]) {
      if (hit(c.name) || hit(c.slug ?? "")) {
        matches.push({
          kind: "tenant",
          id: c.id,
          label: c.name,
          subtitle: c.slug ?? "tenant",
          to: `/tenants/${c.id}/dashboard`,
        });
      }
    }
    for (const m of mcps as McpCatalogueItem[]) {
      if (hit(m.displayName ?? m.name) || hit(m.name)) {
        matches.push({
          kind: "mcp",
          id: m.id,
          label: m.displayName || m.name,
          subtitle: m.transportType.toUpperCase(),
          to: `/mcp-catalogue`,
        });
      }
    }
    for (const s of skills as SkillCatalogueItem[]) {
      if (hit(s.name) || hit(s.description ?? "")) {
        matches.push({
          kind: "skill",
          id: s.id,
          label: s.name,
          subtitle: s.description ?? "skill",
          to: `/skill-catalogue`,
        });
      }
    }
    return matches.slice(0, 40);
  }, [companies, mcps, skills, q]);
}

const kindIcon: Record<CommandResultKind, React.ElementType> = {
  tenant: Building2,
  agent: Bot,
  mcp: Puzzle,
  skill: Zap,
};

const kindLabel: Record<CommandResultKind, string> = {
  tenant: "Tenant",
  agent: "Agent",
  mcp: "MCP Server",
  skill: "Skill",
};

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  initialQuery?: string;
}

export function CommandPalette({ open, onClose, initialQuery = "" }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);
  const results = useCommandPaletteResults(open, query);

  // Focus trap: capture the previously focused element, focus the input on open,
  // and restore focus on close.
  useEffect(() => {
    if (!open) return;
    lastActiveElementRef.current = document.activeElement as HTMLElement | null;
    setQuery(initialQuery);
    setActiveIndex(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      lastActiveElementRef.current?.focus?.();
    };
  }, [open, initialQuery]);

  // Clamp active index when the result set shrinks.
  useEffect(() => {
    if (activeIndex >= results.length) {
      setActiveIndex(Math.max(0, results.length - 1));
    }
  }, [results.length, activeIndex]);

  if (!open) return null;

  const run = (r: CommandResult) => {
    navigate(r.to);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, results.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const r = results[activeIndex];
      if (r) run(r);
      return;
    }
    // Keep focus inside the modal — Tab/Shift+Tab cycles back to input.
    if (e.key === "Tab") {
      e.preventDefault();
      inputRef.current?.focus();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[10vh] motion-safe:transition-opacity"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl border border-border bg-surface shadow-xl">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search size={16} className="text-text-muted" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Search tenants, agents, skills…"
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
          />
          <button
            onClick={onClose}
            aria-label="Close search"
            className="rounded p-1 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
          >
            <X size={14} />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-text-muted">
              {query.trim() ? "No matches." : "Type to search tenants, skills, or MCP servers."}
            </div>
          ) : (
            <ul role="listbox" aria-label="Search results">
              {results.map((r, i) => {
                const Icon = kindIcon[r.kind];
                const isActive = i === activeIndex;
                return (
                  <li key={`${r.kind}-${r.id}`} role="option" aria-selected={isActive}>
                    <button
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => run(r)}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                        isActive
                          ? "bg-accent/10 text-accent"
                          : "text-text-secondary hover:bg-surface-hover",
                      )}
                    >
                      <Icon size={14} className="shrink-0" />
                      <span className="min-w-0 flex-1 truncate font-medium">{r.label}</span>
                      {r.subtitle && (
                        <span className="shrink-0 truncate text-[11px] text-text-muted">
                          {r.subtitle}
                        </span>
                      )}
                      <span className="shrink-0 rounded bg-surface-hover px-1.5 py-0.5 text-[10px] font-medium uppercase text-text-muted">
                        {kindLabel[r.kind]}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-[10px] text-text-muted">
          <span>↑↓ navigate · Enter open · Esc close</span>
          <span>{results.length} result{results.length === 1 ? "" : "s"}</span>
        </div>
      </div>
    </div>
  );
}
